/**
 * Application colour theme.
 *
 * The whole scheme is driven by ONE attribute — `data-theme` on <html>. Every
 * colour lives behind a CSS custom property in `styles/glass.css`, so switching
 * the attribute re-themes the app without a re-render and without any component
 * carrying a light/dark branch.
 *
 * Stored per browser rather than per user record: it is a display preference,
 * not part of the portal's data.
 */

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'blueview-theme';

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

/**
 * The saved preference, falling back to the operating system's setting.
 *
 * A first-time visitor on a dark desktop should not be hit with a white screen,
 * but an explicit choice always wins over the OS.
 */
export function loadTheme(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeMode(stored)) return stored;
  } catch {
    // Private browsing or a blocked storage partition — fall through to the OS.
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Paints the theme. Safe to call repeatedly. */
export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);
  // Tells the browser to draw form controls, scrollbars and the canvas behind
  // the page in the matching scheme; without it they stay stubbornly light.
  root.style.colorScheme = mode;
}

export function saveTheme(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Preference simply will not persist; the applied theme still works.
  }
  applyTheme(mode);
}
